/**
 * Scholarship approvals — the queue the team works through.
 *
 * Each row is one family's application. Approving it records the decision,
 * grants the benefit and lets their scholarship code price out at checkout on
 * both the India and international paths. Rejecting it records the reason.
 *
 * Learner names and emails come from `admin_list_scholarship_applications`,
 * a governed admin-only read — the client never queries profiles directly.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

import PageHead from "@/components/PageHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

type Application = {
  id: string;
  user_id: string;
  status: string;
  requested_benefit_id: string;
  institution_name: string | null;
  answers: Record<string, unknown> | null;
  decision_notes: string | null;
  decided_at: string | null;
  created_at: string;
  learner_name: string | null;
  learner_email: string | null;
};

const STATUSES = ["submitted", "approved", "rejected", "all"] as const;
type StatusFilter = (typeof STATUSES)[number];

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  submitted: "Waiting on us",
  approved: "Approved",
  rejected: "Not approved",
  withdrawn: "Withdrawn",
};

const ANSWER_LABEL: Record<string, string> = {
  income_band: "Family income",
  girl_student: "Girl student",
  first_generation: "First-generation learner",
  ngo_partner: "Referred by an NGO partner",
  merit: "Merit (85%+, award or merit applicant)",
  school: "School or college",
  note: "Anything else",
};

const prettyAnswer = (value: unknown) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const AdminScholarships = () => {
  const [rows, setRows] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>("submitted");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc(
      "admin_list_scholarship_applications",
      { p_status: null },
    );
    if (error) {
      toast.error("Couldn't load the applications.");
    } else {
      setRows((data ?? []) as unknown as Application[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const waiting = rows.filter((r) => r.status === "submitted").length;

  const decide = async (row: Application, approve: boolean) => {
    setBusyId(row.id);
    const { error } = await supabase.rpc("decide_scholarship_application", {
      p_application_id: row.id,
      p_approve: approve,
      p_notes: notes[row.id]?.trim() || null,
    });
    setBusyId(null);
    if (error) {
      toast.error("Couldn't record that decision.");
      return;
    }
    toast.success(
      approve
        ? "Approved — their code will now unlock at checkout."
        : "Recorded as not approved.",
    );
    setNotes((n) => ({ ...n, [row.id]: "" }));
    await load();
  };

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      <PageHead
        title="Scholarship approvals — WeKIT Admin"
        description="Approve or decline scholarship applications."
        path="/admin/scholarships"
        noIndex
      />

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
            <Link to="/admin">
              <ArrowLeft className="mr-1 h-4 w-4" /> Admin hub
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">
            Scholarship approvals
          </h1>
          <p className="text-sm text-muted-foreground">
            {waiting === 0
              ? "Nothing is waiting on a decision right now."
              : `${waiting} application${waiting === 1 ? "" : "s"} waiting on a decision.`}
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
        <TabsList className="mb-4 h-auto flex-wrap">
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} className="min-h-11">
              {s === "all" ? "All" : STATUS_LABEL[s]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {loading ? "Loading…" : "Nothing here yet."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visible.map((row) => (
            <Card key={row.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      {row.learner_name || "Learner"}
                    </CardTitle>
                    <CardDescription>
                      {row.learner_email || "No email on file"} ·{" "}
                      {new Date(row.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{row.requested_benefit_id}</Badge>
                    <Badge
                      variant={
                        row.status === "approved"
                          ? "default"
                          : row.status === "rejected"
                            ? "destructive"
                            : "outline"
                      }
                    >
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  {Object.entries(row.answers ?? {}).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <dt className="text-muted-foreground">
                        {ANSWER_LABEL[key] ?? key}:
                      </dt>
                      <dd className="font-medium">{prettyAnswer(value)}</dd>
                    </div>
                  ))}
                  {row.institution_name && (
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground">School:</dt>
                      <dd className="font-medium">{row.institution_name}</dd>
                    </div>
                  )}
                </dl>

                {row.status === "submitted" ? (
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Input
                      placeholder="Note for the record (optional)"
                      value={notes[row.id] ?? ""}
                      onChange={(e) =>
                        setNotes((n) => ({ ...n, [row.id]: e.target.value }))
                      }
                      aria-label={`Decision note for ${row.learner_name || "this learner"}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        className="min-h-11"
                        onClick={() => decide(row, true)}
                        disabled={busyId === row.id}
                      >
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="min-h-11"
                        onClick={() => decide(row, false)}
                        disabled={busyId === row.id}
                      >
                        <X className="mr-1 h-4 w-4" /> Decline
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {row.decided_at
                      ? `Decided ${new Date(row.decided_at).toLocaleDateString()}`
                      : ""}
                    {row.decision_notes ? ` · ${row.decision_notes}` : ""}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminScholarships;
